import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import TextareaAutosize from 'react-textarea-autosize';
import debounce from 'lodash.debounce';
import { DarkButton } from 'components/Buttons';
import { useMutation } from '@apollo/react-hooks';
import { MessageType, utils } from 'kb-shared';
import { themes } from 'kb-shared';
import { useTranslation } from 'react-i18next';
import Honeybadger from '@honeybadger-io/js';

import { ReactComponent as MessagesIcon } from '../../assets/icons/messaging.svg';
import { MessageAttachmentUpload } from './MessageAttachmentUpload';
import { uploadFile, CATEGORY, OTHER_ID_TYPES } from '../../api/pf2/files';

const { getLabInfo } = utils;

const createConversation = gql`
  mutation createConversation($category: String!, $subject: String!, $body: String!) {
    createConversation(category: $category, subject: $subject, body: $body) {
      conversation {
        id
        messages {
          id
        }
      }
    }
  }
`;

interface Props {
  afterSend: () => void;
  category?: string;
  subject?: string;
  message?: string;
}

const NewConversationInput = ({ subject, message, category, afterSend }: Props) => {
  const { t: translate } = useTranslation();
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const [sendMessage] = useMutation<MessageType>(createConversation);
  const { number } = getLabInfo('NONE', process.env.REACT_APP_ENV);
  const [listOfAttachments, setListOfAttachments] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const DELAY_ON_SEND = 300;

  useEffect(() => {
    // @ts-ignore
    if (subjectRef && subjectRef.current) subjectRef.current.value = subject;
    // @ts-ignore
    if (bodyRef && bodyRef.current) bodyRef.current.value = message;
  }, []);

  function onDeleteAttachment(idx: number) {
    listOfAttachments.splice(idx, 1);
    setListOfAttachments([...listOfAttachments]);
  }

  function uploadAllFiles(messageId: number): Promise<unknown> {
    const promiseUploadFiles = listOfAttachments.map(file =>
      uploadFile(file, CATEGORY.MESSAGE_ATTACHMENT, messageId, OTHER_ID_TYPES.MESSAGE_ID)
    );

    return Promise.all(promiseUploadFiles);
  }

  const onSendMessage = debounce(async () => {
    try {
      const subject = subjectRef.current.value;
      const text = bodyRef.current.value;

      if (subject.trim().length === 0 || text.trim().length === 0) {
        setError('Please make sure that the subject and message have text and try again.');
        return;
      }

      const { data } = await sendMessage({
        variables: {
          category: category,
          subject: subject,
          body: text
        }
      });

      const [message] = data.createConversation.conversation.messages;
      await uploadAllFiles(message.id);

      afterSend();
      setError('');
    } catch (error) {
      Honeybadger.notify(error);
      setError(error);
    }
  }, DELAY_ON_SEND);

  return (
    <Container>
      <Bubble>
        <MessagesIcon />
        <span>
          {translate('messages.new_message.reply_hint')} <a href={`tel:${number}`}>call us</a>
        </span>
      </Bubble>
      <SubjectInputWrapper
        onClick={() => {
          // @ts-ignore
          subjectRef.current.focus();
        }}
      >
        <SubjectInput name="subject" ref={subjectRef} placeholder="Subject" autoComplete="off" />
      </SubjectInputWrapper>
      <BodyInputWrapper
        onClick={() => {
          // @ts-ignore
          bodyRef.current.focus();
        }}
      >
        <Textarea inputRef={bodyRef} />
        <SendButton onClick={onSendMessage}>Send</SendButton>
      </BodyInputWrapper>
      {error && (
        <ErrorContainer className="error">
          <b>Error:</b> {error}
        </ErrorContainer>
      )}
      <MessageAttachmentUpload
        listOfAttachments={listOfAttachments}
        setListOfAttachments={setListOfAttachments}
        onDeleteAttachment={onDeleteAttachment}
      />
    </Container>
  );
};

export default NewConversationInput;

const WhiteInput = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  background: white;
  color: ${({ theme }) => theme.colors.night};
  padding: 1em;
  width: -webkit-fill-available;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const SubjectInputWrapper = styled(WhiteInput)`
  border-radius: 15px 15px 0 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayLight};
`;

const SubjectInput = styled.input`
  border: none;
  width: 100%;

  &:focus {
    border: none;
  }
`;

const ErrorContainer = styled.div`
  color: ${themes.colors.KBError};
  padding: 10px;
  margin: 10px 0px;
  border-radius: 15px;
  background-color: ${themes.colors.KBWhite};
`;

const BodyInputWrapper = styled(WhiteInput)`
  border-radius: 0 0 15px 15px;
`;

const Container = styled.div`
  margin: 1.5em;
  @media (min-width: ${({ theme }) => theme.breakpoints.largePhonePortrait}) {
    margin: 2em;
  }
`;

const SendButton = styled(DarkButton)`
  min-width: fit-content;
  height: fit-content;
  padding: 5px 10px;
`;

const Textarea = styled(TextareaAutosize)`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  border: none;
  width: -webkit-fill-available;
  min-height: 50px;
  resize: none;

  &:focus {
    outline: none;
  }
`;

const Bubble = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  background-color: ${({ theme }) => theme.colors.beigeLight};
  padding: 1em;
  border-radius: 15px;
  width: fit-content;
  white-space: pre-wrap;
  margin: 1.5em auto;
  font-size: smaller;
  display: flex;
  align-items: center;

  a {
    text-decoration: underline;
  }

  svg {
    margin-right: 5px;
  }
`;
